import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Upload, FileText, ExternalLink } from "lucide-react";

const PRODUCT_OPTIONS = [
  { slug: "purified-water", label: "Purified Water" },
  { slug: "alkaline-water", label: "Alkaline Water" },
  { slug: "spring-water", label: "Spring Water" },
  { slug: "sparkling-water", label: "Sparkling Water" },
  { slug: "cbd-water", label: "CBD Water" },
  { slug: "cbd-water-pets", label: "CBD Water for Pets" },
];

const emptyForm = { slug: "", label: "", icon: "", color: "", description: "", document_url: "", status: "active", display_order: 100 };
const emptyAssign = { product_slug: "", badge_slugs: [] };

const AdminComplianceBadges = () => {
  const [badges, setBadges] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [assignForm, setAssignForm] = useState(emptyAssign);
  const [showAssign, setShowAssign] = useState(false);
  const [docUploading, setDocUploading] = useState(false);

  const load = () => {
    api.get("/admin/compliance-badges").then((r) => setBadges(r.data.badges || [])).catch(() => toast.error("Failed to load badges"));
    api.get("/admin/product-badges").then((r) => setAssignments(r.data.assignments || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const p = { ...form }; delete p.slug;
        await api.patch(`/admin/compliance-badges/${editing.id}`, p);
      } else {
        await api.post("/admin/compliance-badges", form);
      }
      toast.success("Saved");
      setEditing(null); setShowNew(false); setForm(emptyForm);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.label}"?`)) return;
    try {
      await api.delete(`/admin/compliance-badges/${b.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const toggle = async (b) => {
    const next = b.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/admin/compliance-badges/${b.id}`, { status: next });
      load();
    } catch { toast.error("Update failed"); }
  };

  const openAssign = (slug) => {
    const existing = assignments.find((a) => a.product_slug === slug);
    setAssignForm({ product_slug: slug, badge_slugs: existing?.badge_slugs || [] });
    setShowAssign(true);
  };

  const toggleAssignedBadge = (slug) => {
    setAssignForm((f) => ({
      ...f,
      badge_slugs: f.badge_slugs.includes(slug)
        ? f.badge_slugs.filter((s) => s !== slug)
        : [...f.badge_slugs, slug],
    }));
  };

  const saveAssign = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/product-badges", assignForm);
      toast.success("Assignment saved");
      setShowAssign(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Compliance Badges</div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Compliance & Trust Badges</h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Reusable trust badges (3rd-Party Tested, GMP, FDA Registered, Organic, Vegan, Halal, Kosher, …) that
            appear on any product card. Manage the catalog, then assign per product.
          </p>
        </div>
        <button onClick={() => { setForm(emptyForm); setShowNew(true); }} className="ft-btn-primary" data-testid="new-badge-btn"><Plus size={16}/> New Badge</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="ft-card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--ft-border)] font-semibold">Badge Catalog</div>
          <table className="ft-table" data-testid="badges-table">
            <thead><tr><th>Badge</th><th>Order</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {badges.map((b) => (
                <tr key={b.id} data-testid={`badge-row-${b.slug}`}>
                  <td>
                    <div className="font-semibold text-[var(--ft-black)]">{b.label}</div>
                    <div className="text-xs font-mono text-[var(--ft-muted)]">{b.slug}</div>
                  </td>
                  <td className="text-xs">{b.display_order}</td>
                  <td>
                    <span className={`text-[0.625rem] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${b.status === "active" ? "bg-[var(--ft-gold)] text-[var(--ft-black)]" : "bg-[var(--ft-charcoal)] text-white/80"}`}>
                      {b.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1.5">
                      <button onClick={() => { setEditing(b); setForm({ slug: b.slug, label: b.label, icon: b.icon || "", color: b.color || "", description: b.description || "", document_url: b.document_url || "", status: b.status, display_order: b.display_order }); }} className="text-xs px-2 py-1 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)]" data-testid={`badge-edit-${b.slug}`}>Edit</button>
                      <button onClick={() => toggle(b)} className="text-xs px-2 py-1 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)]" data-testid={`badge-toggle-${b.slug}`}>{b.status === "active" ? "Off" : "On"}</button>
                      <button onClick={() => remove(b)} className="p-1 rounded-full text-[var(--ft-muted)] hover:text-[var(--ft-error)]" data-testid={`badge-del-${b.slug}`}><Trash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ft-card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--ft-border)] font-semibold">Product Assignments</div>
          <table className="ft-table" data-testid="assignments-table">
            <thead><tr><th>Product</th><th>Badges</th><th></th></tr></thead>
            <tbody>
              {PRODUCT_OPTIONS.map((p) => {
                const asg = assignments.find((a) => a.product_slug === p.slug);
                return (
                  <tr key={p.slug} data-testid={`assignment-row-${p.slug}`}>
                    <td>
                      <div className="font-semibold text-[var(--ft-black)] text-sm">{p.label}</div>
                      <div className="text-xs font-mono text-[var(--ft-muted)]">{p.slug}</div>
                    </td>
                    <td className="text-xs">
                      {asg?.badge_slugs?.length ? asg.badge_slugs.join(", ") : <span className="italic text-[var(--ft-muted)]">none</span>}
                    </td>
                    <td>
                      <button onClick={() => openAssign(p.slug)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`assign-${p.slug}`}>Assign</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || showNew) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setShowNew(false); }}>
          <div className="bg-white rounded-2xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold tracking-tight">{editing ? "Edit Badge" : "New Badge"}</h2>
              <button onClick={() => { setEditing(null); setShowNew(false); }}><X size={20}/></button>
            </div>
            <form onSubmit={save} className="space-y-4" data-testid="badge-form">
              <div><label className="ft-label">Label*</label><input required className="ft-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="badge-label" /></div>
              <div><label className="ft-label">Slug*</label><input required disabled={!!editing} className="ft-input font-mono text-sm disabled:opacity-70" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="badge-slug" /></div>
              <div><label className="ft-label">Description (for Trust page)</label><textarea rows={3} className="ft-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="badge-description" /></div>
              <div>
                <label className="ft-label">Certificate / Documentation</label>
                <div className="flex gap-2">
                  <input
                    className="ft-input flex-1"
                    placeholder="Upload a PDF/image or paste a URL"
                    value={form.document_url}
                    onChange={(e) => setForm({ ...form, document_url: e.target.value })}
                    data-testid="badge-document"
                  />
                  <label className="ft-btn-ghost cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5" data-testid="badge-document-upload">
                    <Upload size={14} />
                    <span className="text-xs">{docUploading ? "Uploading…" : "Upload"}</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="application/pdf,image/*"
                      disabled={docUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setDocUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const r = await api.post("/admin/uploads/document?purpose=badge-doc", fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          setForm((f) => ({ ...f, document_url: r.data.url }));
                          toast.success("Document uploaded");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Upload failed");
                        } finally {
                          setDocUploading(false);
                          e.target.value = "";
                        }
                      }}
                    />
                  </label>
                </div>
                <div className="text-xs text-[var(--ft-muted)] mt-1">
                  For rich metadata (title, doc type, effective/expiration date, product & badge assignment), use the{" "}
                  <a className="text-[var(--ft-gold)] font-semibold" href="/admin/trust-documents">Trust Documents</a> manager.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="ft-label">Status</label><select className="ft-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
                <div><label className="ft-label">Order</label><input type="number" className="ft-input" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[var(--ft-border)]">
                <button type="button" onClick={() => { setEditing(null); setShowNew(false); }} className="ft-btn-ghost">Cancel</button>
                <button type="submit" className="ft-btn-primary" data-testid="badge-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAssign(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold tracking-tight">Assign badges → <span className="font-mono text-sm">{assignForm.product_slug}</span></h2>
              <button onClick={() => setShowAssign(false)}><X size={20}/></button>
            </div>
            <form onSubmit={saveAssign} className="space-y-4" data-testid="assign-form">
              <div className="grid grid-cols-2 gap-2">
                {badges.filter((b) => b.status === "active").map((b) => (
                  <label key={b.slug} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border border-[var(--ft-border)] hover:border-[var(--ft-gold)]" data-testid={`assign-check-${b.slug}`}>
                    <input type="checkbox" checked={assignForm.badge_slugs.includes(b.slug)} onChange={() => toggleAssignedBadge(b.slug)} />
                    {b.label}
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[var(--ft-border)]">
                <button type="button" onClick={() => setShowAssign(false)} className="ft-btn-ghost">Cancel</button>
                <button type="submit" className="ft-btn-primary" data-testid="assign-save">Save Assignment</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminComplianceBadges;
